import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Box,
  Chip,
  Tab,
  Tabs,
  Paper,
  Tooltip,
  Collapse,
  Button
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Close, Build, LocalShipping, CheckCircle, Schedule, ExpandMore, ExpandLess } from '@material-ui/icons';
import moment from 'moment';
import axios from 'axios';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: 600,
    maxWidth: 800,
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  serviceItem: {
    borderLeft: '4px solid',
    marginBottom: theme.spacing(1),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  maintenanceService: {
    borderLeftColor: theme.palette.primary.main,
  },
  otherService: {
    borderLeftColor: theme.palette.secondary.main,
  },
  serviceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  crewChip: {
    marginLeft: theme.spacing(1),
  },
  tabContent: {
    paddingTop: theme.spacing(2),
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  sectionHeader: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  }
}));

const ServiceHistoryModal = ({ open, onClose, site, url }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && site?._id) {
      fetchServiceHistory();
    }
  }, [open, site]);

  const fetchServiceHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      let fetchURL = `${url}/api/jobs/findSitesWorkorders/${site._id}/${50}`
      console.log('Get our sites recent workorder history...', fetchURL)
      // Fetch work orders for this site - note: using GET with params
      const response = await axios.get(fetchURL);

      // Process the response data
      const { mtcWorkorders = [], workorders = [] } = response.data;
      
      // Process all workorders (both maintenance and other)
      const allWorkorders = [...mtcWorkorders, ...workorders];
      
      // Remove duplicates (mtcWorkorders might be in both arrays)
      const uniqueWorkorders = Array.from(
        new Map(allWorkorders.map(wo => [wo._id, wo])).values()
      );
      
      const processedHistory = uniqueWorkorders
        .filter(wo => wo.status !== 'Cancelled')
        .map(workorder => {
          const scheduledDate = workorder.scheduledDate || workorder.scheduled || workorder.work_complete_at;
          console.log('wo..', workorder)
          return {
            ...workorder,
            isMaintenance: workorder.serviceCode === '1200',
            displayDate: workorder.work_complete_at 
              ? moment(workorder.work_complete_at).format('MMM DD, YYYY')
              : moment(scheduledDate).format('MMM DD, YYYY'),
            dayOfWeek: moment(scheduledDate).format('dddd'),
            weekType: moment(scheduledDate).week() % 2 === 0 ? 'Even' : 'Odd',
            crewName: workorder.crewName || 'Unassigned',
            crewColor: '#ccc', // We'll need to fetch crew colors separately if needed
            technician: workorder?.work_completed_by?._id ? `${workorder.work_completed_by.firstName} ${workorder.work_completed_by.lastName}` : 'N/A',
            duration: workorder.actHours || ((workorder.estHours || 0) + ((workorder.estMinutes || 0) / 60)),
            serviceType: workorder.serviceName || workorder.serviceCategory || 'Service',
            workorderNumber: workorder.number ? `WO-${workorder.number}` : `WO-${workorder._id.slice(-6)}`,
            jobName: workorder.jobName || workorder.name || workorder.serviceDescription || workorder.serviceName || 'Service',
            isFieldComplete: workorder.workComplete === true, // Field completed the work
            isOfficeComplete: workorder.status === 'Completed', // Office completed their part
            // Service visit stats (if available from backend)
            serviceStats: workorder.serviceStats || workorder.stats?.[0] || null
          };
        })
        .sort((a, b) => {
          // Sort by completion date first, then scheduled date
          const dateA = new Date(a.work_complete_at || a.completedDate || a.scheduledDate || a.scheduled);
          const dateB = new Date(b.work_complete_at || b.completedDate || b.scheduledDate || b.scheduled);
          return dateB - dateA; // Most recent first
        });

      setServiceHistory(processedHistory);
    } catch (err) {
      console.error('Error fetching service history:', err);
      setError('Failed to load service history');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const maintenanceServices = serviceHistory.filter(s => s.isMaintenance);
  const otherServices = serviceHistory.filter(s => !s.isMaintenance);

  const [expandedItems, setExpandedItems] = useState({});

  const handleExpandClick = (serviceId) => {
    setExpandedItems(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  const renderServiceItem = (service) => {
    const Icon = service.isMaintenance ? Build : LocalShipping;
    const isExpanded = expandedItems[service._id];
    
    // Determine status icon and color
    let StatusIcon = Schedule;
    let statusColor = 'action';
    let statusTooltip = 'Scheduled';
    
    if (service.isFieldComplete && service.isOfficeComplete) {
      StatusIcon = CheckCircle;
      statusColor = 'primary';
      statusTooltip = 'Completed';
    } else if (service.isFieldComplete && !service.isOfficeComplete) {
      StatusIcon = CheckCircle;
      statusColor = 'secondary';
      statusTooltip = 'Field Complete (Pending Office)';
    }
    
    return (
      <>
      <ListItem 
        key={service._id} 
        className={`${classes.serviceItem} ${service.isMaintenance ? classes.maintenanceService : classes.otherService}`}
      >
        <ListItemIcon>
          <Icon color={service.isMaintenance ? 'primary' : 'secondary'} />
        </ListItemIcon>
        <ListItemText
          primary={
            <div className={classes.serviceHeader}>
              <Typography variant="subtitle1">
                {service.workorderNumber} - {service.displayDate} - {service.dayOfWeek}
                {service.isMaintenance && (
                  <Chip 
                    size="small" 
                    label={`${service.weekType} Week`}
                    color="primary"
                    className={classes.crewChip}
                  />
                )}
              </Typography>
              <div>
                {service.crewName && (
                  <Chip 
                    size="small" 
                    label={service.crewName}
                    className={classes.crewChip}
                    style={{ backgroundColor: service.crewColor || '#ccc' }}
                  />
                )}
                <Tooltip title={statusTooltip}>
                  <StatusIcon 
                    color={statusColor} 
                    fontSize="small" 
                    style={{ marginLeft: 8 }} 
                  />
                </Tooltip>
                {service.serviceStats?.totalVisits > 1 && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExpandClick(service._id);
                    }}
                    style={{ marginLeft: 8 }}
                  >
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                )}
              </div>
            </div>
          }
          secondary={
            <div>
              <Typography variant="body2" color="textSecondary">
                <strong>{service.jobName}</strong>
                {service.status && ` • Status: ${service.status}`}
              </Typography>
              {service.serviceStats ? (
                <Tooltip title={
                  <div>
                    <Typography variant="caption" display="block" style={{ fontWeight: 'bold' }}>
                      Service Visit Details:
                    </Typography>
                    <Typography variant="caption" display="block">
                      Avg per visit: {service.serviceStats.avgHoursWorked?.toFixed(1) || 0} hrs
                    </Typography>
                    <Typography variant="caption" display="block">
                      Avg man-hours: {service.serviceStats.avgManHours?.toFixed(1) || 0}
                    </Typography>
                    {service.serviceStats.totalTravelTime > 0 && (
                      <Typography variant="caption" display="block">
                        Total travel time: {(service.serviceStats.totalTravelTime / 60).toFixed(1)} hrs
                      </Typography>
                    )}
                    {service.serviceStats.avgTimeOnSite && (
                      <Typography variant="caption" display="block">
                        Avg time on site: {(service.serviceStats.avgTimeOnSite / (1000 * 60 * 60)).toFixed(1)} hrs
                      </Typography>
                    )}
                    {service.serviceStats.totalLaborCost && (
                      <Typography variant="caption" display="block">
                        Total labor cost: ${service.serviceStats.totalLaborCost.toFixed(2)}
                      </Typography>
                    )}
                    {service.serviceStats.employeesInvolved?.length > 0 && (
                      <Typography variant="caption" display="block">
                        {service.serviceStats.employeesInvolved.length} unique employees involved
                      </Typography>
                    )}
                    {service.serviceStats.notesCount > 0 && (
                      <Typography variant="caption" display="block" style={{ color: '#ff9800' }}>
                        📝 {service.serviceStats.notesCount} visit{service.serviceStats.notesCount > 1 ? 's' : ''} with notes
                      </Typography>
                    )}
                  </div>
                }>
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 4, cursor: 'help' }}>
                    📊 Actual: {service.serviceStats.totalVisits} visit{service.serviceStats.totalVisits > 1 ? 's' : ''} 
                    • {service.serviceStats.totalHoursWorked?.toFixed(1) || 0} hrs worked
                    • {service.serviceStats.totalManHours?.toFixed(1) || 0} man-hrs
                    {service.serviceStats.crewsUsed?.length > 1 && ` • ${service.serviceStats.crewsUsed.length} crews`}
                    {service.serviceStats.totalTravelTime > 0 && ` • ${(service.serviceStats.totalTravelTime / 60).toFixed(1)} hrs travel`}
                    {service.serviceStats.notesCount > 0 && ' 📝'}
                  </Typography>
                </Tooltip>
              ) : null}
              <Typography variant="body2" color="textSecondary">
                  {service.technician && `Technician: ${service.technician} • `}
                  Est. Duration: {service.duration?.toFixed(1) || 0} hours
                </Typography>
            </div>
          }
        />
      </ListItem>
      {service.serviceStats?.visitDates && service.serviceStats.totalVisits > 1 && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box pl={9} pr={2} pb={2} style={{ backgroundColor: '#f5f5f5' }}>
            <Typography variant="subtitle2" gutterBottom>
              Visit Breakdown:
            </Typography>
            {service.serviceStats.visitDates.map((visit, index) => (
              <Box key={index} mb={1} p={1} style={{ backgroundColor: 'white', borderRadius: 4 }}>
                <Typography variant="caption" display="block">
                  Visit {index + 1} - {moment(visit.date).format('MMM DD, YYYY')}
                  {visit.onSite && visit.offSite && (
                    <span> • {moment(visit.onSite).format('h:mm A')} - {moment(visit.offSite).format('h:mm A')}</span>
                  )}
                  {visit.hoursWorked && <span> • {visit.hoursWorked.toFixed(1)} hrs</span>}
                </Typography>
                {visit.notes && (
                  <Typography variant="caption" display="block" style={{ fontStyle: 'italic', marginTop: 4 }}>
                    📝 {visit.notes}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Collapse>
      )}
    </>
    );
  };

  const renderEmptyState = (message) => (
    <div className={classes.emptyState}>
      <Typography variant="body1">{message}</Typography>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Service History - {site?.name}
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : error ? (
          <div className={classes.emptyState}>
            <Typography color="error">{error}</Typography>
          </div>
        ) : (
          <>
            <Paper square>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label={`Maintenance (${maintenanceServices.length})`} />
                <Tab label={`Other Services (${otherServices.length})`} />
                <Tab label="All Services" />
              </Tabs>
            </Paper>
            
            <div className={classes.tabContent}>
              {tabValue === 0 && (
                <Box>
                  {maintenanceServices.length > 0 ? (
                    <>
                      <div className={classes.sectionHeader}>
                        <Typography variant="subtitle2">
                          Most Recent Maintenance Services
                        </Typography>
                      </div>
                      <List>
                        {maintenanceServices.slice(0, 10).map(renderServiceItem)}
                      </List>
                    </>
                  ) : (
                    renderEmptyState('No maintenance service history found')
                  )}
                </Box>
              )}
              
              {tabValue === 1 && (
                <Box>
                  {otherServices.length > 0 ? (
                    <>
                      <div className={classes.sectionHeader}>
                        <Typography variant="subtitle2">
                          Other Services (Irrigation, Pest Control, etc.)
                        </Typography>
                      </div>
                      <List>
                        {otherServices.slice(0, 10).map(renderServiceItem)}
                      </List>
                    </>
                  ) : (
                    renderEmptyState('No other service history found')
                  )}
                </Box>
              )}
              
              {tabValue === 2 && (
                <Box>
                  {serviceHistory.length > 0 ? (
                    <>
                      <div className={classes.sectionHeader}>
                        <Typography variant="subtitle2">
                          All Service History
                        </Typography>
                      </div>
                      <List>
                        {serviceHistory.slice(0, 20).map(renderServiceItem)}
                      </List>
                    </>
                  ) : (
                    renderEmptyState('No service history found')
                  )}
                </Box>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ServiceHistoryModal;